import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Typography, Box, Paper, Card, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  List, ListItem, ListItemIcon, ListItemText, useTheme,
  LinearProgress, Divider, Chip, Button, IconButton, Badge, Stack, Skeleton, Alert
} from '@mui/material';
import {
  People as PeopleIcon, ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon, AssignmentTurnedIn as AssignmentTurnedInIcon,
  HourglassEmpty as HourglassEmptyIcon, Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon, MoreVert as MoreVertIcon,
  ArrowDropUp as ArrowDropUpIcon, ArrowDropDown as ArrowDropDownIcon,
  Refresh as RefreshIcon, CalendarToday as CalendarIcon,
  Add as AddIcon, FileDownload as FileDownloadIcon, Assessment as AssessmentIcon
} from '@mui/icons-material';
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer, Sector, BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';
import { alpha } from '@mui/material/styles';

// --- MOCK DATA (Fallback for skeleton and other components) ---
const mockDashboardData = {
  pieChartData: [
    { name: 'Completed', value: 400 },
    { name: 'In Progress', value: 300 },
    { name: 'On Hold', value: 300 },
    { name: 'Pending', value: 200 },
  ],
  barChartData: [
    { name: 'Jan', count: 12 },
    { name: 'Feb', count: 8 },
    { name: 'Mar', count: 15 },
    { name: 'Apr', count: 20 },
    { name: 'May', count: 10 },
    { name: 'Jun', count: 14 },
  ],
  tasks: [
    { id: 1, title: 'Finalize project proposal', completed: false, priority: 'high' },
    { id: 2, title: 'Review marketing report', completed: true, priority: 'medium' },
    { id: 3, title: 'Schedule team meeting', completed: false, priority: 'low' },
    { id: 4, title: 'Update product documentation', completed: false, priority: 'high' },
  ],
  notifications: [
    { id: 1, text: 'New order received #12345', time: '2 min ago', read: false },
    { id: 2, text: 'Database backup completed', time: '1 hour ago', read: true },
    { id: 3, text: 'System update available', time: '3 hours ago', read: true },
    { id: 4, text: 'New customer registration', time: '5 hours ago', read: false },
  ],
  tableColumns: [
    { id: 'task', label: 'Task' },
    { id: 'priority', label: 'Priority' },
    { id: 'status', label: 'Status' },
    { id: 'assigned', label: 'Assigned To' },
    { id: 'progress', label: 'Progress' },
  ],
  tableRows: [
    { task: 'Product A', priority: 'high', status: 'Almost Done', assigned: 'John Doe', progress: 80 },
    { task: 'Product B', priority: 'medium', status: 'Out of Stock', assigned: 'Sarah Smith', progress: 0 },
  ],
  recentActivities: [
    { id: 1, user: 'John Doe', action: 'created a new project', time: '10 min ago' },
    { id: 2, user: 'Sarah Smith', action: 'updated sales report', time: '25 min ago' },
    { id: 3, user: 'Mike Johnson', action: 'completed task #123', time: '1 hour ago' },
  ]
};

// --- API ENDPOINTS ---
const API_ENDPOINTS = {
  COUNTS: 'http://localhost:5890/admin/dashboard/counts',
  PROJECT_STATUS: 'http://localhost:5890/project-status-count',
  TASKS: 'http://localhost:5890/tasks'
};

// --- SKELETON COMPONENTS ---
const CountCardSkeleton = () => (
  <Card sx={{ p: 2.5, borderRadius: '12px', height: 190, boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)' }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
      <Skeleton variant="circular" width={44} height={44} />
      <Skeleton variant="rectangular" width={24} height={24} />
    </Box>
    <Box>
      <Skeleton variant="text" sx={{ fontSize: 'h4.fontSize', mb: 0.5 }} />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="40%" sx={{ mt: 1.5 }} />
    </Box>
  </Card>
);

const ChartSkeleton = () => (
  <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: '12px', border: '1px solid divider', boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)' }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width="40%" sx={{ fontSize: 'h6.fontSize' }} />
      <Skeleton variant="rectangular" width={80} height={30} sx={{ borderRadius: '8px' }} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={300} />
  </Paper>
);

const ListSkeleton = () => (
  <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: '12px', border: '1px solid divider', boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)' }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width="40%" sx={{ fontSize: 'h6.fontSize' }} />
      <Skeleton variant="rectangular" width={60} height={30} sx={{ borderRadius: '8px' }} />
    </Box>
    <List dense>
      {Array.from(new Array(4)).map((_, index) => (
        <ListItem key={index} sx={{ py: 1.5, px: 2, mb: 1 }}>
          <ListItemIcon sx={{ minWidth: '36px' }}>
            <Skeleton variant="circular" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={<Skeleton variant="text" width="80%" />}
            secondary={<Skeleton variant="text" width="40%" />}
          />
        </ListItem>
      ))}
    </List>
  </Paper>
);

const TableSkeleton = () => (
  <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: '12px', border: '1px solid divider', boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)' }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width="40%" sx={{ fontSize: 'h6.fontSize' }} />
      <Skeleton variant="rectangular" width={80} height={30} sx={{ borderRadius: '8px' }} />
    </Box>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {Array.from(new Array(4)).map((_, index) => (
              <TableCell key={index}><Skeleton variant="text" /></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(new Array(5)).map((_, index) => (
            <TableRow key={index}>
              {Array.from(new Array(4)).map((_, cellIndex) => (
                <TableCell key={cellIndex}><Skeleton variant="text" /></TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

// --- NEW COMPONENT FOR COUNT ANIMATION ---
const CountUp = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (value === undefined || value === null) return;
    const start = 0;
    const end = parseInt(value, 10);
    if (start === end) {
      setCount(end);
      return;
    }

    let startTime = null;
    const animateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const step = Math.min(progress / duration, 1);
      setCount(Math.floor(step * (end - start) + start));

      if (progress < duration) {
        window.requestAnimationFrame(animateCount);
      } else {
        setCount(end);
      }
    };
    window.requestAnimationFrame(animateCount);
  }, [value, duration]);

  return <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>{count}</Typography>;
};

// --- UPDATED COUNT CARD COMPONENT ---
const CountCard = ({ title, count, icon, trend, color }) => {
  const isPositive = trend?.startsWith('+');
  return (
    <Card
      sx={{
        minHeight: 190,
        display: 'flex',
        flexDirection: 'column',
        p: 2.5,
        borderRadius: '12px',
        width: "300px",
        boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
        borderLeft: `4px solid ${color}`,
        transition: 'transform 0.3s, box-shadow 0.3s',
        background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        }
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Avatar sx={{
          bgcolor: alpha(color, 0.1),
          color: color,
          width: 44,
          height: 44,
          boxShadow: `0 2px 8px ${alpha(color, 0.2)}`
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 20 } })}
        </Avatar>
        <IconButton size="small" sx={{ color: 'text.secondary' }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box flexGrow={1}>
        <CountUp value={count} />
        <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
        {trend && (
          <Box display="flex" alignItems="center" mt="auto">
            {isPositive ? (
              <ArrowDropUpIcon sx={{ color: 'success.main' }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'error.main' }} />
            )}
            <Typography variant="caption" sx={{
              color: isPositive ? 'success.main' : 'error.main',
              fontWeight: 'medium'
            }}>
              {trend}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

const PieChartCard = ({ data, title }) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);

  // Dynamic colors for the pie chart
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.text.secondary
  ];

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent } = props;
    return (
      <g>
        <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill="#333" fontSize={18} fontWeight="bold">
          {payload.name}
        </text>
        <text x={cx} y={cy + 10} dy={8} textAnchor="middle" fill="#999">
          {`${(percent * 100).toFixed(2)}%`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  return (
    <Paper elevation={0} sx={{
      p: 3,
      height: '100%',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: 'divider',
      background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)'
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          Refresh
        </Button>
      </Box>
      <Box sx={{ height: 300, width: '100%' }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
              onMouseEnter={onPieEnter}
            >
              {data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [value, 'Count']}
              contentStyle={{
                borderRadius: '8px',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                border: 'none',
                background: theme.palette.background.paper
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box display="flex" justifyContent="center" flexWrap="wrap" mt={2}>
        {data?.map((entry, index) => (
          <Chip
            key={index}
            label={entry.name}
            size="small"
            sx={{
              m: 0.5,
              pl: 0.5,
              backgroundColor: alpha(COLORS[index % COLORS.length], 0.1),
              color: theme.palette.text.primary,
              '& .MuiChip-icon': {
                color: COLORS[index % COLORS.length],
                ml: 0.5
              }
            }}
            icon={
              <Box
                width={12}
                height={12}
                bgcolor={COLORS[index % COLORS.length]}
                borderRadius="2px"
              />
            }
          />
        ))}
      </Box>
    </Paper>
  );
};

const BarChartCard = ({ data, title }) => {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={{
      p: 3,
      height: '100%',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: 'divider',
      background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)'
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Button
          size="small"
          startIcon={<CalendarIcon />}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          2023
        </Button>
      </Box>
      <Box sx={{ height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={data}>
            <XAxis
              dataKey="name"
              tick={{ fill: theme.palette.text.secondary }}
              axisLine={false}
            />
            <YAxis
              tick={{ fill: theme.palette.text.secondary }}
              axisLine={false}
              label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
            />
            <Tooltip
              contentStyle={{
                borderRadius: '8px',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                border: 'none',
                background: theme.palette.background.paper
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px'
              }}
            />
            <Bar
              dataKey="count"
              fill={theme.palette.primary.main}
              radius={[4, 4, 0, 0]}
              name="Project Count"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const TaskList = ({ tasks }) => {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={{
      p: 3,
      height: '100%',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: 'divider',
      background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)'
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Recent Tasks
        </Typography>
        <Button
          size="small"
          sx={{
            textTransform: 'none',
            color: 'text.secondary'
          }}
        >
          View All
        </Button>
      </Box>
      <List dense sx={{ mb: 2 }}>
        {tasks?.map((task) => (
          <ListItem
            key={task.id}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: '8px'
              },
              py: 1.5,
              px: 2,
              mb: 1,
              backgroundColor: task.completed ? alpha('#4caf50', 0.05) : 'inherit'
            }}
            secondaryAction={
              <Chip
                label={task.priority}
                size="small"
                color={
                  task.priority === 'high' ? 'error' :
                  task.priority === 'medium' ? 'warning' : 'default'
                }
                variant="outlined"
                sx={{
                  borderRadius: '4px',
                  fontWeight: 'medium'
                }}
              />
            }
          >
            <ListItemIcon sx={{ minWidth: '36px' }}>
              {task.completed ? (
                <AssignmentTurnedInIcon color="success" />
              ) : (
                <HourglassEmptyIcon color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={task.title}
              primaryTypographyProps={{
                sx: {
                  textDecoration: task.completed ? 'line-through' : 'none',
                  color: task.completed ? 'text.disabled' : 'text.primary',
                  fontSize: '0.875rem'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
  
    </Paper>
  );
};

const Notifications = ({ notifications }) => {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={{
      p: 3,
      height: '100%',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: 'divider',
      background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)'
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Notifications
        </Typography>
        <Badge
          badgeContent={notifications.filter(n => !n.read).length}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              right: -5,
              top: 5,
              border: `2px solid ${theme.palette.background.paper}`,
              padding: '0 4px',
            }
          }}
        >
          <NotificationsIcon color="action" />
        </Badge>
      </Box>
      <List dense sx={{ mb: 2 }}>
        {notifications?.map((notification) => (
          <ListItem
            key={notification.id}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: '8px'
              },
              py: 1.5,
              px: 2,
              mb: 1,
              bgcolor: !notification.read ? alpha(theme.palette.primary.main, 0.05) : 'inherit',
              borderLeft: !notification.read ? `3px solid ${theme.palette.primary.main}` : 'none'
            }}
          >
            <ListItemText
              primary={notification.text}
              secondary={notification.time}
              primaryTypographyProps={{
                sx: {
                  fontWeight: !notification.read ? 'medium' : 'normal',
                  fontSize: '0.875rem'
                }
              }}
              secondaryTypographyProps={{
                sx: {
                  fontSize: '0.75rem'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
      <Button
        fullWidth
        sx={{
          textTransform: 'none',
          borderRadius: '8px',
          py: 1,
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        View All Notifications
      </Button>
    </Paper>
  );
};

const RecentActivities = ({ activities }) => {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={{
      p: 3,
      borderRadius: '12px',
      border: '1px solid',
      borderColor: 'divider',
      background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)',
      height: '100%'
    }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Recent Activities
      </Typography>
      <List dense>
        {activities?.map((activity) => (
          <ListItem
            key={activity.id}
            sx={{
              py: 1.5,
              px: 2,
              mb: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: '8px'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Avatar sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                fontSize: '0.875rem'
              }}>
                {activity.user.charAt(0)}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <React.Fragment>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 'medium' }}>
                    {activity.user}
                  </Typography>
                  {` ${activity.action}`}
                </React.Fragment>
              }
              secondary={activity.time}
              secondaryTypographyProps={{
                sx: {
                  fontSize: '0.75rem'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

const DataTable = ({ columns, rows }) => {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={{
      p: 3,
      height: '100%',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: 'divider',
      background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)',
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.03)'
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Recent Tasks
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          Refresh
        </Button>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns?.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row, index) => (
              <TableRow
                hover
                key={index}
                sx={{
                  '&:last-child td': {
                    borderBottom: 'none'
                  }
                }}
              >
                {columns?.map((column) => (
                  <TableCell key={column.id}>
                    {column.id === 'status' ? (
                      <Chip
                        label={row[column.id]}
                        size="small"
                        color={row[column.id] === 'Completed' ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{
                          borderRadius: '4px',
                          fontWeight: 'medium'
                        }}
                      />
                    ) : column.id === 'progress' ? (
                      <Box display="flex" alignItems="center">
                        <Box width="100%" mr={1}>
                          <LinearProgress
                            variant="determinate"
                            value={row[column.id]}
                            color={row[column.id] === 100 ? 'success' : 'primary'}
                            sx={{
                              height: 6,
                              borderRadius: 3
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {`${row[column.id]}%`}
                        </Typography>
                      </Box>
                    ) : (
                      row[column.id]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// --- MAIN DASHBOARD COMPONENT ---
function ProjectDashboard() {
  const [countCardsData, setCountCardsData] = useState([]);
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [taskStatusData, setTaskStatusData] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  // Fetch dashboard counts data
  const fetchCountsData = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.COUNTS);
      if (!response.ok) {
        throw new Error('Failed to fetch counts data');
      }
      const data = await response.json();
      
      const transformedData = [
        { 
          title: 'Total Developers', 
          count: data.developerCount || 0, 
          icon: <PeopleIcon />,  
          color: '#1a73e8' 
        },
        { 
          title: 'Total Users', 
          count: data.userCount || 0, 
          icon: <PeopleIcon />,  
          color: '#34a853' 
        },
        { 
          title: 'Total Customers', 
          count: data.customerCount || 0, 
          icon: <PeopleIcon />,  
          color: '#f9ab00' 
        },
      ];
      
      setCountCardsData(transformedData);
    } catch (err) {
      console.error('Error fetching counts data:', err);
      setError('Failed to load dashboard counts. Using mock data instead.');
      setCountCardsData([
        { title: 'Total Developers', count: 120, icon: <PeopleIcon />, trend: '+15%', color: '#1a73e8' },
        { title: 'Total Users', count: 500, icon: <AttachMoneyIcon />, trend: '-5%', color: '#34a853' },
        { title: 'Total Customers', count: 750, icon: <ShoppingCartIcon />, trend: '+20%', color: '#f9ab00' }
      ]);
    }
  };

  // Fetch project status data for the first pie chart
  const fetchProjectStatusData = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PROJECT_STATUS);
      if (!response.ok) {
        throw new Error('Failed to fetch project status data');
      }
      const data = await response.json();
      
      const transformedPieData = [
        { name: 'Completed', value: data.Completed || 0 },
        { name: 'In Progress', value: data["In Progress"] || 0 },
        { name: 'On Hold', value: data["On Hold"] || 0 },
        { name: 'Not Started', value: data["Not Started"] || 0 },
        { name: 'Cancelled', value: data.Cancelled || 0 },
      ];
      
      setProjectStatusData(transformedPieData);
    } catch (err) {
      console.error('Error fetching project status data:', err);
      setError('Failed to load project status data. Using mock data instead.');
      setProjectStatusData(mockDashboardData.pieChartData);
    }
  };

  // Fetch task data for the data table and task status pie chart
  const fetchTasksData = async () => {
    try {
        const response = await fetch(API_ENDPOINTS.TASKS);
        if (!response.ok) {
            throw new Error('Failed to fetch tasks data');
        }
        const data = await response.json();
        
        // Process data for the task list and table
        const transformedTasks = data.map(task => ({
            id: task._id,
            title: task.task,
            completed: task.status === 'Completed' || task.progress === 100,
            priority: task.priority,
            task: task.task,
            status: task.status,
            assigned: task.assigned,
            progress: task.progress
        }));

        setTaskData(transformedTasks);

        // Process data for the new task status pie chart
        const statusCounts = {};
        data.forEach(task => {
            statusCounts[task.status] = (statusCounts[task.status] || 0) + 1;
        });

        const transformedTaskStatusData = Object.keys(statusCounts).map(status => ({
            name: status,
            value: statusCounts[status]
        }));

        setTaskStatusData(transformedTaskStatusData);
    } catch (err) {
        console.error('Error fetching tasks data:', err);
        setError('Failed to load tasks data. Using mock data instead.');
        setTaskData(mockDashboardData.tasks);
        setTaskStatusData(mockDashboardData.pieChartData);
    }
  };

  // Load all data
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchCountsData(),
        fetchProjectStatusData(),
        fetchTasksData()
      ]);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load some dashboard data. Using mock data where necessary.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = () => {
    loadData();
  };

  const recentTasksColumns = [
    { id: 'task', label: 'Task' },
    { id: 'priority', label: 'Priority' },
    { id: 'status', label: 'Status' },
    { id: 'assigned', label: 'Assigned To' },
    { id: 'progress', label: 'Progress' },
  ];
  
  // Combine all the separate refresh handlers into a single function.
  const handleCardRefresh = (refreshFunction) => {
    // Implement refresh logic for individual cards if needed.
    // For now, we'll just use the main `loadData` function.
    loadData();
  };

  return (
    <Box sx={{
      flexGrow: 1,
      p: 1,
      backgroundColor: '#f9fafc',
      minHeight: '100vh',
      backgroundImage: 'linear-gradient(to bottom, #f9fafc, #f0f4f8)'
    }}>
      <Container maxWidth="xl">
        {/* Header with Title and Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" component="h1" sx={{
              fontWeight: 'bold',
              color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
              letterSpacing: '-0.5px',
              mb: 0.5
            }}>
              Project Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              A quick overview of your key metrics and recent activities.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                px: 3
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              onClick={handleRefresh}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                boxShadow: '0 2px 10px rgba(63, 81, 181, 0.3)'
              }}
            >
              Refresh Data
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Top Count Cards */}
        <Grid container spacing={3} mb={4}>
          {isLoading ? (
            Array.from(new Array(3)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <CountCardSkeleton />
              </Grid>
            ))
          ) : (
            countCardsData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <CountCard
                  title={item.title}
                  count={item.count}
                  icon={item.icon}
                  trend={item.trend}
                  color={item.color}
                />
              </Grid>
            ))
          )}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <PieChartCard title="Project Status" data={projectStatusData} />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <BarChartCard title="Projects per Month" data={mockDashboardData.barChartData} />
            )}
          </Grid>
        </Grid>

  
   <br />

   
          <Grid item xs={12} md={8}>
            {isLoading ? (
              <TableSkeleton />
            ) : (
              <DataTable
                columns={recentTasksColumns}
                rows={taskData}
              />
            )}
          </Grid>
      

      </Container>
    </Box>
  );
}

export default ProjectDashboard;